import { useState } from 'react'
import { computeCalibrationState } from '../calibration'
import type {
  FormPoint,
  Game,
  Prediction,
  QuestId,
  QuestPickState,
  Rival,
  RivalRequest,
  League,
  SeasonStats,
  TeamResult,
  UpcomingFixture,
  UserProfile,
} from '../types'
import type { QuestResult } from '../questResults'
import type { FixtureState } from './OtherPlayerGameweekReveal'
import QuestPickerSheet from './QuestPickerSheet'
import type { TeamId } from '../teams'
import type { AvatarPresetId } from '../storage'
import ProfileHeader from './profile/ProfileHeader'
import SkillMeter from './profile/SkillMeter'
import ProfileRecords from './profile/ProfileRecords'
import type { BestGameweekRecord, ChipTile } from './profile/records'
import { useScoreCountUp } from './profile/useScoreCountUp'
import {
  OwnProfileSections,
  VisitedProfileSections,
} from './profile/ProfileSections'
import RivalsModal from './profile/RivalsModal'
import EndRivalryConfirmModal from './profile/EndRivalryConfirmModal'
import AvatarPickerModal from './profile/AvatarPickerModal'
import { useRulesScreen } from '../features/rules/rulesContext'
import './Profile.css'

interface ProfileProps {
  profile: UserProfile
  leagues: League[]
  recentResults: TeamResult[]
  upcomingFixtures: UpcomingFixture[]
  overallScore: number
  /** Points collected in the current gameweek so far — 0 before it's
   * revealed anything, holds at the final total once every game in the
   * gameweek has been played. Only meaningful for 'self'; left
   * unset (defaults to 0) for read-only 'other' views, which have no live
   * current-gameweek data source. */
  currentGameweekPoints?: number
  /** Fixtures of the current gameweek whose results are in, and how many there
   * are in total. Feeds the total-points hero's meta row only:
   * mid-gameweek it reads "4 of 10 in", otherwise "GW 15 of 38". Left unset for
   * read-only 'other' views, which have no live current-gameweek source and so
   * always show the gameweek form. */
  settledFixtures?: number
  totalFixtures?: number
  seasonStats: SeasonStats
  formHistory: FormPoint[]
  /** The season's best gameweek plus its per-fixture marks, or null before GW1 settles — the
   *  Sharpest week row is absent then, not a row of dashes. */
  bestGameweek?: BestGameweekRecord | null
  /** Every finalized gameweek of the profile being viewed, for the sharpest-week row's arrows to
   *  step through. Only another player's profile passes it: your own modal walks your season. */
  sharpestWeekWalk?: number[]
  /** The three chip tiles, already derived. Empty renders no Chips row — which is what a visited
   *  profile whose server didn't send a chip ledger gets, never the viewer's own chips. */
  chipTiles?: ChipTile[]
  /** Opens the gameweek results modal at `gameweek`, through the app's history controller.
   *  `walk`, when given, is every gameweek the modal's arrows should step through — a chip's
   *  uses rather than the season. Absent leaves the records block inert. */
  onOpenGameweek?: (gameweek: number, walk?: number[]) => void
  /** The gameweek in play right now. Only used to place this profile's
   * calibration window before they've been scored in anything —
   * once they have, the window comes from their own scored gameweeks. */
  currentGameweek?: number
  /** 'other' renders a read-only view of someone else's profile. */
  viewMode?: 'self' | 'other'
  /** The other player's "GW {N} Calls" reveal — only rendered
   * in `viewMode='other'`, and only while `canRevealOtherPlayerGameweek`
   * says the target gameweek is in its post-deadline/pre-next-deadline
   * window. Undefined/null means outside that window: the section is
   * absent entirely, not shown empty or blurred. */
  otherPlayerGameweekReveal?: {
    gameweek: number
    games: Game[]
    predictions: Record<string, Prediction>
    bankerGameId: string | null
    activeChipId: string | null
    fixtureStates: Record<string, FixtureState>
    questPicks: Partial<Record<QuestId, QuestPickState>>
    questResults: Partial<Record<QuestId, QuestResult>>
    questsEnabled: QuestId[]
  } | null
  onBack?: () => void
  /** Only meaningful in 'other' view mode, for this profile's own Challenge/Pending/Rival button. */
  rivalStatus?: 'none' | 'pending' | 'rival'
  /** Challenges or ends a rivalry with the profile being viewed, depending on `rivalStatus`. */
  onRivalAction?: () => void
  /** Only used in 'self' view mode, for the homepage Rivals section. */
  rivals?: Rival[]
  incomingRivalRequests?: RivalRequest[]
  onViewRivalProfile?: (userId: string, displayName: string) => void
  onAcceptRivalRequest?: (requestId: string) => void
  onDeclineRivalRequest?: (requestId: string) => void
  /** Only used in 'self' view mode, for the Settings button at the bottom of the page. */
  onOpenSettings?: () => void
  /** Only used in 'self' view mode — sets the favourite team when one is
   * picked from the shield's team-picker sheet. */
  onChangeFavouriteTeam?: (team: TeamId) => void
  /** The avatar to render — in 'self' view the signed-in user's server-backed
   * selection, in 'other' view the viewed player's. Null shows
   * the initials fallback. */
  avatarPreset?: AvatarPresetId | null
  /** Only used in 'self' view mode — persists a newly-picked avatar
   * server-side so other players see it too. */
  onChangeAvatarPreset?: (preset: AvatarPresetId) => void
}

function Profile({
  profile,
  leagues,
  overallScore,
  currentGameweekPoints = 0,
  settledFixtures = 0,
  totalFixtures = 0,
  seasonStats,
  formHistory,
  bestGameweek = null,
  sharpestWeekWalk,
  chipTiles = [],
  onOpenGameweek,
  currentGameweek,
  viewMode = 'self',
  otherPlayerGameweekReveal = null,
  onBack,
  rivalStatus = 'none',
  onRivalAction,
  rivals = [],
  incomingRivalRequests = [],
  onViewRivalProfile,
  onAcceptRivalRequest,
  onDeclineRivalRequest,
  onOpenSettings,
  onChangeFavouriteTeam,
  avatarPreset: avatarPresetProp = null,
  onChangeAvatarPreset,
}: ProfileProps) {
  const [isRivalsListOpen, setIsRivalsListOpen] = useState(false)
  const [isConfirmingEndRivalry, setIsConfirmingEndRivalry] = useState(false)
  const [isAvatarPickerOpen, setIsAvatarPickerOpen] = useState(false)
  const [isTeamPickerOpen, setIsTeamPickerOpen] = useState(false)
  const { open: openRules } = useRulesScreen()
  // Avatar comes straight from the caller (server-backed for 'self', the
 // viewed player's server value for 'other'), not device
  // localStorage. No local copy: a self-view pick calls onChangeAvatarPreset,
  // which updates App state synchronously so this prop reflects it on the very
  // next render — instant feedback without a state-sync effect.
  const avatarPreset = avatarPresetProp
  const isReadOnly = viewMode === 'other'
  // Owned here rather than inside ProfileHeader because two siblings read it: the hero renders
  // the ticking numeral, and the records block below waits for it to land.
  const countUp = useScoreCountUp(overallScore, !isReadOnly)
  // Form history has exactly one point per gameweek this profile has been
  // scored in (auto-predicted ones included), which is precisely what
  // calibration counts — never the season's elapsed gameweeks.
  const calibration = computeCalibrationState(
    formHistory.map((point) => point.gameweek),
    currentGameweek
  )

  return (
    <>
      <div className="profile">
        <ProfileHeader
          profile={profile}
          currentGameweekPoints={currentGameweekPoints}
          currentGameweek={currentGameweek ?? 1}
          settledFixtures={settledFixtures}
          totalFixtures={totalFixtures}
          isReadOnly={isReadOnly}
          countUp={countUp}
          avatarPreset={avatarPreset}
          rivals={rivals}
          incomingRivalRequests={incomingRivalRequests}
          onBack={onBack}
          onOpenSettings={onOpenSettings}
          onOpenAvatarPicker={() => setIsAvatarPickerOpen(true)}
          onOpenTeamPicker={() => setIsTeamPickerOpen(true)}
          onOpenRivalsList={() => setIsRivalsListOpen(true)}
        />

        <SkillMeter
          seasonStats={seasonStats}
          profileKey={profile.name}
          calibration={calibration}
          isSelf={!isReadOnly}
        />

        <ProfileRecords
          bestGameweek={bestGameweek}
          sharpestWeekWalk={sharpestWeekWalk}
          chips={chipTiles}
          onOpenGameweek={onOpenGameweek}
        />

        {/* The two variants are named branches, not a run of inline `isReadOnly &&`
            checks. They now differ by more than a hidden button — one carries every settings
            surface in the app and the other must carry none — and a reader has to be able to see
            which is which without tracing five separate ternaries down the file. */}
        {isReadOnly ? (
          <VisitedProfileSections
            leagues={leagues}
            gameweekReveal={otherPlayerGameweekReveal}
            rivalStatus={rivalStatus}
            onRivalAction={onRivalAction}
            onConfirmEndRivalry={() => setIsConfirmingEndRivalry(true)}
          />
        ) : (
          <OwnProfileSections
            onOpenHowToPlay={() => openRules()}
            onOpenSettings={onOpenSettings}
          />
        )}
      </div>

      {isRivalsListOpen && onViewRivalProfile && (
        <RivalsModal
          rivals={rivals}
          incomingRivalRequests={incomingRivalRequests}
          onClose={() => setIsRivalsListOpen(false)}
          onViewRivalProfile={onViewRivalProfile}
          onAcceptRivalRequest={onAcceptRivalRequest}
          onDeclineRivalRequest={onDeclineRivalRequest}
        />
      )}

      {isConfirmingEndRivalry && (
        <EndRivalryConfirmModal
          rivalName={profile.name}
          onCancel={() => setIsConfirmingEndRivalry(false)}
          onConfirm={() => {
            setIsConfirmingEndRivalry(false)
            onRivalAction?.()
          }}
        />
      )}

      {isAvatarPickerOpen && (
        <AvatarPickerModal
          avatarPreset={avatarPreset}
          onClose={() => setIsAvatarPickerOpen(false)}
          onSelect={(preset) => onChangeAvatarPreset?.(preset)}
        />
      )}

      {isTeamPickerOpen && (
        <QuestPickerSheet
          type="team"
          allTeams
          selectedTeam={profile.favouriteTeam ?? undefined}
          onSelectTeam={(team) => onChangeFavouriteTeam?.(team)}
          onClose={() => setIsTeamPickerOpen(false)}
        />
      )}
    </>
  )
}

export default Profile
